/**
 * Chamadas HTTP das ausências e das visitas.
 *
 * Papel: aqui ficam as oito chamadas de `/api/calendario/ausencias` e
 * `/api/calendario/visitas` — listar, criar, alterar e excluir cada um dos dois
 * cadastros de período.
 *
 * Como usar: as duas listagens usam o mesmo recorte dos dias, e trazem tudo que
 * **encosta** no período: um intervalo que começa antes e termina depois entra,
 * porque cobre os dias do meio. Pedir só o que começa dentro faria férias de
 * duas semanas sumirem da segunda quinzena.
 */
import {
  type Ausencia,
  type NovaAusencia,
  type NovaVisita,
  type RecorteDoCalendario,
  type Visita,
  ausenciaSchema,
  visitaSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

const listaDeAusenciasSchema = z.array(ausenciaSchema);
const listaDeVisitasSchema = z.array(visitaSchema);

/**
 * Lê as ausências que encostam num período.
 *
 * @param recorte - A casa e as duas pontas do período.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As ausências, cada uma com o colaborador aninhado.
 */
export function buscarAusencias(
  recorte: RecorteDoCalendario,
  sinal?: AbortSignal,
): Promise<Ausencia[]> {
  return clienteApi.get('/calendario/ausencias', {
    schema: listaDeAusenciasSchema,
    params: recorte,
    sinal,
  });
}

/**
 * Cria uma ausência.
 *
 * @param dados - O período preenchido, já validado.
 * @returns A ausência como ficou gravada.
 */
export function criarAusencia(dados: NovaAusencia): Promise<Ausencia> {
  return clienteApi.post('/calendario/ausencias', dados, { schema: ausenciaSchema });
}

/**
 * Substitui todos os campos de uma ausência.
 *
 * @param id - Identificador da linha em `calendario_ausencias`.
 * @param dados - A ausência inteira, e não apenas os campos alterados.
 * @returns A ausência como ficou gravada.
 */
export function atualizarAusencia(id: string, dados: NovaAusencia): Promise<Ausencia> {
  return clienteApi.put(`/calendario/ausencias/${encodeURIComponent(id)}`, dados, {
    schema: ausenciaSchema,
  });
}

/**
 * Apaga uma ausência.
 *
 * Não recebe schema porque a API responde 204 sem corpo. Os dias que o período
 * cobria voltam a cobrar situação de quem estava ausente.
 *
 * @param id - Identificador da linha em `calendario_ausencias`.
 */
export function excluirAusencia(id: string): Promise<void> {
  return clienteApi.remover(`/calendario/ausencias/${encodeURIComponent(id)}`);
}

/**
 * Lê as visitas que encostam num período.
 *
 * @param recorte - A casa e as duas pontas do período.
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns As visitas, cada uma com a residência aninhada.
 */
export function buscarVisitas(
  recorte: RecorteDoCalendario,
  sinal?: AbortSignal,
): Promise<Visita[]> {
  return clienteApi.get('/calendario/visitas', {
    schema: listaDeVisitasSchema,
    params: recorte,
    sinal,
  });
}

/**
 * Cria uma visita.
 *
 * @param dados - A visita preenchida, já validada.
 * @returns A visita como ficou gravada.
 */
export function criarVisita(dados: NovaVisita): Promise<Visita> {
  return clienteApi.post('/calendario/visitas', dados, { schema: visitaSchema });
}

/**
 * Substitui todos os campos de uma visita.
 *
 * @param id - Identificador da linha em `calendario_visitas`.
 * @param dados - A visita inteira, e não apenas os campos alterados.
 * @returns A visita como ficou gravada.
 */
export function atualizarVisita(id: string, dados: NovaVisita): Promise<Visita> {
  return clienteApi.put(`/calendario/visitas/${encodeURIComponent(id)}`, dados, {
    schema: visitaSchema,
  });
}

/**
 * Apaga uma visita.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `calendario_visitas`.
 */
export function excluirVisita(id: string): Promise<void> {
  return clienteApi.remover(`/calendario/visitas/${encodeURIComponent(id)}`);
}
